<template>
  <b-card no-body>
    <div class="m-2 d-flex flex-wrap align-items-center justify-content-between">
      <div>
        <h4 class="mb-25">Saldo Stock</h4>
        <small class="text-muted">Saldo akhir per produk dan warehouse setelah penerimaan, retur, order, dan adjustment.</small>
      </div>

      <div class="d-flex flex-wrap align-items-center mt-1 mt-md-0">
        <b-button variant="primary" class="mr-1 mb-50 mb-sm-0" @click="openAdjustmentModal">
          Adjustment Stock
        </b-button>
        <b-button
          variant="success"
          class="mr-1 mb-50 mb-sm-0"
          :disabled="isExporting"
          @click="exportStock"
        >
          {{ isExporting ? 'Menyiapkan...' : 'Export Excel' }}
        </b-button>
        <v-select
          v-model="perPage"
          :options="perPageOptions"
          :clearable="false"
          class="per-page-selector mr-1 mb-50 mb-sm-0"
        />
        <b-form-input
          v-model="searchQuery"
          class="search-input"
          placeholder="Cari kode, produk, atau warehouse"
        />
      </div>
    </div>

    <b-table
      ref="refStockTable"
      :items="fetchStock"
      :fields="fields"
      :per-page="perPage"
      :sort-by.sync="sortBy"
      :sort-desc.sync="isSortDirDesc"
      responsive
      show-empty
      empty-text="Data stock tidak ditemukan"
      class="mb-0"
    >
      <template #cell(stock_akhir)="data">
        <span :class="Number(data.value) < 0 ? 'text-danger' : 'text-success'" class="font-weight-bolder">
          {{ Number(data.value) }}
        </span>
      </template>
    </b-table>

    <div class="mx-2 my-2 d-flex flex-wrap align-items-center justify-content-between">
      <small class="text-muted">Menampilkan {{ dataMeta.from }}-{{ dataMeta.to }} dari {{ totalRows }} data</small>
      <b-pagination
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        first-number
        last-number
        class="mb-0 mt-1 mt-sm-0"
      />
    </div>

    <b-modal
      v-model="showAdjustmentModal"
      title="Adjustment Stock"
      :ok-disabled="isSavingAdjustment"
      :cancel-disabled="isSavingAdjustment"
      ok-title="Simpan Adjustment"
      cancel-title="Batal"
      @ok="saveAdjustment"
    >
      <b-form @submit.prevent="saveAdjustment">
        <b-form-group label="Warehouse" label-for="adjustment-warehouse">
          <b-form-select
            id="adjustment-warehouse"
            v-model="adjustmentForm.warehouse_id"
            :options="warehouseOptions"
            value-field="id"
            text-field="warehouse_name"
          >
            <template #first>
              <b-form-select-option :value="null">Pilih warehouse</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>
        <b-form-group label="Kode Produk" label-for="adjustment-product">
          <b-form-input
            id="adjustment-product"
            v-model.trim="adjustmentForm.kd_produk"
            placeholder="Contoh: ID6800CG"
            required
          />
        </b-form-group>
        <b-form-group label="Jenis Adjustment" label-for="adjustment-type">
          <b-form-select
            id="adjustment-type"
            v-model="adjustmentForm.type"
            :options="adjustmentTypes"
          />
        </b-form-group>
        <b-form-group label="Kuantiti" label-for="adjustment-quantity">
          <b-form-input
            id="adjustment-quantity"
            v-model.number="adjustmentForm.quantity"
            type="number"
            min="1"
            required
          />
        </b-form-group>
        <b-form-group label="Tanggal Adjustment" label-for="adjustment-date">
          <b-form-input
            id="adjustment-date"
            v-model="adjustmentForm.adjusted_at"
            type="datetime-local"
          />
        </b-form-group>
        <b-form-group label="Keterangan" label-for="adjustment-note">
          <b-form-textarea
            id="adjustment-note"
            v-model.trim="adjustmentForm.note"
            rows="3"
            placeholder="Alasan adjustment stock"
          />
        </b-form-group>
      </b-form>
      <template #modal-ok>
        <b-spinner v-if="isSavingAdjustment" small class="mr-50" />
        Simpan Adjustment
      </template>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BButton,
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BFormTextarea,
  BModal,
  BPagination,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import { computed, ref, watch } from '@vue/composition-api'
import vSelect from 'vue-select'
import axios from '@axios'

const defaultAdjustment = () => ({
  warehouse_id: null,
  kd_produk: '',
  type: 'IN',
  quantity: null,
  adjusted_at: new Date().toISOString().slice(0, 16),
  note: '',
})

export default {
  components: {
    BButton,
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormSelectOption,
    BFormTextarea,
    BModal,
    BPagination,
    BSpinner,
    BTable,
    vSelect,
  },
  setup(props, { root }) {
    const refStockTable = ref(null)
    const currentPage = ref(1)
    const perPage = ref(25)
    const totalRows = ref(0)
    const searchQuery = ref('')
    const sortBy = ref('stock_akhir')
    const isSortDirDesc = ref(true)
    const isExporting = ref(false)
    const isSavingAdjustment = ref(false)
    const showAdjustmentModal = ref(false)
    const warehouseOptions = ref([])
    const adjustmentForm = ref(defaultAdjustment())
    const perPageOptions = [25, 50, 100]
    const adjustmentTypes = [
      { value: 'IN', text: 'IN - Tambah Stock' },
      { value: 'OUT', text: 'OUT - Kurangi Stock' },
    ]
    const fields = [
      { key: 'kd_produk', label: 'Kode Produk', sortable: true },
      { key: 'nama_produk', label: 'Nama Produk', sortable: true },
      { key: 'warehouse', label: 'Warehouse', sortable: true },
      { key: 'stock_akhir', label: 'Stock Akhir', sortable: true },
    ]

    const refresh = () => refStockTable.value?.refresh()
    watch([currentPage, perPage, searchQuery, sortBy, isSortDirDesc], () => refresh())

    const fetchStock = (ctx, callback) => {
      axios.get('/api/auth/report/stock-list', {
        params: {
          page: currentPage.value,
          perPage: perPage.value,
          q: searchQuery.value,
          sortBy: sortBy.value,
          sortDesc: isSortDirDesc.value,
        },
      }).then(response => {
        totalRows.value = response.data.total
        callback(response.data.data)
      }).catch(() => callback([]))
    }

    const exportStock = () => {
      isExporting.value = true
      axios.get('/api/auth/report/stock-export', {
        params: {
          q: searchQuery.value,
          sortBy: sortBy.value,
          sortDesc: isSortDirDesc.value,
        },
        responseType: 'blob',
      }).then(response => {
        const url = URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.download = 'saldo-stock.xlsx'
        link.click()
        URL.revokeObjectURL(url)
      }).finally(() => {
        isExporting.value = false
      })
    }

    const loadWarehouses = () => {
      axios.get('/api/auth/warehouse-commerce', {
        params: { limit: 100, sort_by: 'warehouse_name', direction: 'asc' },
      }).then(response => {
        warehouseOptions.value = response.data.data || []
      })
    }

    const openAdjustmentModal = () => {
      adjustmentForm.value = defaultAdjustment()
      showAdjustmentModal.value = true
      if (!warehouseOptions.value.length) loadWarehouses()
    }

    const saveAdjustment = event => {
      event.preventDefault()
      isSavingAdjustment.value = true
      axios.post('/api/auth/stock-adjustments', adjustmentForm.value)
        .then(() => {
          showAdjustmentModal.value = false
          refresh()
          root.$bvToast.toast('Adjustment stock berhasil disimpan.', {
            title: 'Stock Diperbarui',
            variant: 'success',
            solid: true,
          })
        })
        .catch(error => {
          root.$bvToast.toast(error.response?.data?.message || 'Adjustment stock gagal disimpan.', {
            title: 'Gagal Menyimpan Adjustment',
            variant: 'danger',
            solid: true,
          })
        })
        .finally(() => {
          isSavingAdjustment.value = false
        })
    }

    const dataMeta = computed(() => {
      const from = totalRows.value ? (currentPage.value - 1) * perPage.value + 1 : 0
      const to = Math.min(currentPage.value * perPage.value, totalRows.value)
      return { from, to }
    })

    return {
      refStockTable,
      currentPage,
      perPage,
      totalRows,
      searchQuery,
      sortBy,
      isSortDirDesc,
      isExporting,
      isSavingAdjustment,
      showAdjustmentModal,
      warehouseOptions,
      adjustmentForm,
      perPageOptions,
      adjustmentTypes,
      fields,
      fetchStock,
      exportStock,
      openAdjustmentModal,
      saveAdjustment,
      dataMeta,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector { min-width: 92px; }
.search-input { width: 260px; }
@media (max-width: 575px) { .search-input { width: 190px; } }
</style>

<style lang="scss">
@import '~@core/scss/vue/libs/vue-select.scss';
</style>
